import { useState, useEffect, useMemo } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import {
  ClockCounterClockwise, ArrowClockwise, Envelope, Eye, CheckCircle, PaperPlaneRight,
  User, Files, MagnifyingGlass, SignIn, Robot, Funnel, CurrencyDollar,
} from "@phosphor-icons/react";

/* Wave 15 P4 — VA Activity Feed
   Live timeline of every action taken by read-only admins (VAs) plus a
   rollup card showing 24h/7d counts per VA. Founder-only glance page. */

const ICON_BY_ACTION = {
  reply_sent: PaperPlaneRight,
  reply_marked_seen: Eye,
  reply_classified: Robot,
  template_applied: Files,
  template_created: Files,
  template_edited: Files,
  lead_reviewed: MagnifyingGlass,
  onboarding_step: CheckCircle,
  login: SignIn,
};

const ACCENT_BY_ACTION = {
  reply_sent: "text-emerald-400 bg-emerald-500/10",
  reply_marked_seen: "text-neutral-300 bg-white/[0.06]",
  reply_classified: "text-purple-300 bg-purple-500/10",
  template_applied: "text-[#FF9764] bg-[#FF5A1F]/10",
  template_created: "text-[#FF9764] bg-[#FF5A1F]/10",
  template_edited: "text-[#FF9764] bg-[#FF5A1F]/10",
  lead_reviewed: "text-sky-300 bg-sky-500/10",
  onboarding_step: "text-emerald-300 bg-emerald-500/10",
  login: "text-neutral-400 bg-white/[0.04]",
};

function _relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function _describe(item) {
  const meta = item.meta || {};
  switch (item.action) {
    case "reply_sent":
      return `to ${meta.company || meta.lead_email || "a hot lead"}`;
    case "template_applied":
      return `used "${meta.template_name || "a template"}"`;
    case "reply_marked_seen":
      return `on reply ${(meta.reply_id || "").slice(0, 8)}`;
    case "onboarding_step":
      return `step: ${(meta.step_id || "").replace(/_/g, " ")}`;
    default:
      return "";
  }
}

// Colour-band the score: green (great), amber (ok), red (needs work).
// Also shows the LLM rationale on hover so the founder can drill in without
// clicking. Score of 0 is treated as valid data, not "missing".
function QualityBadge({ quality }) {
  const score = quality?.score;
  if (score == null) return null;
  let cls = "border-red-500/40 text-red-300 bg-red-500/10";
  if (score >= 70) cls = "border-emerald-500/40 text-emerald-300 bg-emerald-500/10";
  else if (score >= 50) cls = "border-amber-500/40 text-amber-300 bg-amber-500/10";
  const rationale = quality?.rationale || "";
  return (
    <span
      title={rationale}
      data-testid="va-activity-quality-badge"
      className={`inline-flex items-center gap-1 uppercase tracking-widest text-[9px] font-semibold rounded-full border px-1.5 py-0.5 ${cls}`}
    >
      Q · {score}
    </span>
  );
}

export default function VAActivityPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [qualityBoard, setQualityBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionKinds, setActionKinds] = useState({});
  const [filterAction, setFilterAction] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (filterAction) params.set("action", filterAction);
      if (filterEmail) params.set("admin_email", filterEmail);

      const [feedRes, sumRes, qualRes] = await Promise.all([
        client.get(`/admin/va-activity/feed?${params.toString()}`),
        client.get("/admin/va-activity/summary"),
        client.get("/admin/va-activity/quality/leaderboard?days=7"),
      ]);
      setItems(feedRes.data.items || []);
      setActionKinds(feedRes.data.action_kinds || {});
      setSummary(sumRes.data);
      setQualityBoard(qualRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load VA activity");
    } finally {
      setLoading(false);
    }
  };

  const sendDigestNow = async () => {
    if (!window.confirm("Force-send the VA weekly digest to your Slack + admin emails right now (bypasses the Monday 8am schedule)?")) return;
    setSendingDigest(true);
    try {
      const { data } = await client.post("/admin/va-activity/digest/send-now");
      const emailBits = `${data.email_sent_count}/${data.email_recipients} emails sent`;
      const slackBits = data.slack_posted ? "Slack ✓" : `Slack skipped (${data.slack_reason || "no webhook"})`;
      toast.success(`Digest fired — ${emailBits} · ${slackBits}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => { load(); }, [filterAction, filterEmail]); // eslint-disable-line

  // Distinct VA emails from the 7d summary (for the filter dropdown)
  const vaEmails = useMemo(() => {
    const list = Object.keys(summary?.last_7d || {});
    if (filterEmail && !list.includes(filterEmail)) list.push(filterEmail);
    return list;
  }, [summary, filterEmail]);

  const totals7d = useMemo(() => {
    const per = summary?.last_7d || {};
    const out = {};
    for (const email of Object.keys(per)) {
      out[email] = Object.values(per[email]).reduce((a, b) => a + b, 0);
    }
    return out;
  }, [summary]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" data-testid="va-activity-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF9764] font-semibold mb-2">
            <ClockCounterClockwise size={14} weight="fill" /> VA Activity
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">
            What your team's been doing
          </h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            A live timeline of every action your read-only admins take: replies sent, templates applied, onboarding checkpoints. Refreshes on load; glance-check once a day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/app/admin/va-bonus"
            data-testid="va-activity-bonus-link"
            className="text-xs flex items-center gap-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 px-3 py-1.5"
          >
            <CurrencyDollar size={12} weight="bold" /> Bonus tracker
          </a>
          <button
            onClick={sendDigestNow}
            disabled={sendingDigest}
            data-testid="va-activity-send-digest-btn"
            className="text-xs flex items-center gap-1.5 rounded-full bg-[#FF5A1F]/10 hover:bg-[#FF5A1F]/20 border border-[#FF5A1F]/30 text-[#FF9764] px-3 py-1.5 disabled:opacity-50"
          >
            <PaperPlaneRight size={12} weight="bold" /> {sendingDigest ? "Sending…" : "Send weekly digest now"}
          </button>
          <button
            onClick={load}
            data-testid="va-activity-refresh-btn"
            className="text-xs flex items-center gap-1.5 rounded-full border border-white/10 hover:border-white/25 px-3 py-1.5 text-neutral-300"
          >
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {/* Per-VA rollup */}
      {summary && Object.keys(summary.last_7d || {}).length > 0 && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="va-activity-summary-grid"
        >
          {Object.entries(summary.last_7d).map(([email, breakdown]) => (
            <div
              key={email}
              className="card-surface rounded-xl border border-white/10 p-4 hover:border-white/20 cursor-pointer transition-colors"
              onClick={() => setFilterEmail(filterEmail === email ? "" : email)}
              data-testid={`va-summary-card-${email}`}
            >
              <div className="flex items-center gap-2 text-white font-medium text-sm truncate">
                <User size={14} weight="fill" className="text-neutral-500 shrink-0" />
                <span className="truncate">{email}</span>
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-semibold text-white">
                  {totals7d[email] || 0}
                </span>
                <span className="text-xs text-neutral-500">actions · 7d</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(breakdown).map(([action, count]) => (
                  <span
                    key={action}
                    className="text-[10px] uppercase tracking-widest bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5 text-neutral-400"
                    title={actionKinds[action] || action}
                  >
                    {(action || "").replace(/_/g, " ")} {count}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Quality Leaderboard — trailing 7 days */}
      {qualityBoard && qualityBoard.rows && qualityBoard.rows.length > 0 && (
        <div
          data-testid="va-quality-leaderboard"
          className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
              Reply Quality · Last 7 days
            </div>
            <div className="text-[10px] text-neutral-500">
              (AI-graded 0-100 · hover badges for the critique)
            </div>
          </div>
          <div className="space-y-2">
            {qualityBoard.rows.map((r, i) => (
              <div
                key={r.admin_email}
                data-testid={`va-quality-row-${r.admin_email}`}
                className="flex items-center gap-3 text-sm"
              >
                <span className="text-[11px] text-neutral-500 font-mono w-5 text-center">#{i + 1}</span>
                <span className="text-neutral-200 flex-1 truncate">{r.admin_email}</span>
                <span className="text-[11px] text-neutral-500 font-mono tabular-nums shrink-0">
                  {r.reply_count} {r.reply_count === 1 ? "reply" : "replies"}
                </span>
                <span
                  className={`text-sm font-mono font-semibold tabular-nums shrink-0 w-14 text-right ${
                    r.avg_score >= 70 ? "text-emerald-300"
                    : r.avg_score >= 50 ? "text-amber-300"
                    : "text-red-300"
                  }`}
                >
                  {r.avg_score}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <Funnel size={14} weight="bold" className="text-neutral-500" />
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          data-testid="va-activity-filter-action"
          className="bg-white/[0.04] border border-white/10 rounded-md px-2 py-1.5 text-neutral-300"
        >
          <option value="">All action types</option>
          {Object.entries(actionKinds).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <select
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          data-testid="va-activity-filter-email"
          className="bg-white/[0.04] border border-white/10 rounded-md px-2 py-1.5 text-neutral-300"
        >
          <option value="">All VAs</option>
          {vaEmails.map((email) => (
            <option key={email} value={email}>{email}</option>
          ))}
        </select>
        {(filterAction || filterEmail) && (
          <button
            onClick={() => { setFilterAction(""); setFilterEmail(""); }}
            data-testid="va-activity-clear-filters"
            className="text-neutral-400 hover:text-white underline text-xs"
          >
            clear
          </button>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-sm text-neutral-500 py-10 text-center">Loading feed…</div>
      ) : items.length === 0 ? (
        <div
          className="card-surface rounded-xl border border-white/10 p-8 text-center"
          data-testid="va-activity-empty"
        >
          <ClockCounterClockwise size={28} weight="regular" className="mx-auto text-neutral-600 mb-2" />
          <p className="text-sm text-neutral-400">
            No VA activity yet
            {filterAction || filterEmail ? " matching these filters" : ""}
            . Once you hire a read-only admin and they start replying to hot leads, every action shows up here.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="va-activity-timeline">
          {items.map((item, i) => {
            const Icon = ICON_BY_ACTION[item.action] || ClockCounterClockwise;
            const accent = ACCENT_BY_ACTION[item.action] || "text-neutral-400 bg-white/[0.04]";
            const desc = _describe(item);
            return (
              <div
                key={item.id || i}
                className="flex items-start gap-3 rounded-lg hover:bg-white/[0.02] px-3 py-2.5 transition-colors"
                data-testid={`va-activity-item-${item.id || i}`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${accent}`}>
                  <Icon size={14} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    <span className="text-neutral-500 font-medium">{item.admin_email}</span>
                    <span className="text-neutral-600 mx-1.5">·</span>
                    <span>{item.label || item.action}</span>
                    {desc && (
                      <span className="text-neutral-500 ml-1">— {desc}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-600 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{_relTime(item.created_at)}</span>
                    {item.is_readonly && (
                      <span className="uppercase tracking-widest text-[9px] text-purple-400/70 border border-purple-500/20 rounded-full px-1.5 py-0.5">
                        Read-only
                      </span>
                    )}
                    {item.action === "reply_sent" && item.meta?.quality?.score != null && (
                      <QualityBadge quality={item.meta.quality} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
